import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import { assessmentNotesService, AssessmentNote, CreateNoteData } from '../services/assessmentNotesService';
import LoadingSpinner from './LoadingSpinner';

interface AssessmentNotesProps {
  assessmentId: number;
}

const AssessmentNotes: React.FC<AssessmentNotesProps> = ({ assessmentId }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingNote, setEditingNote] = useState<AssessmentNote | null>(null);
  const [newNote, setNewNote] = useState<CreateNoteData>({
    noteType: 'general',
    content: '',
  });

  const queryClient = useQueryClient();

  // Fetch notes
  const { data: notesData, isLoading } = useQuery({
    queryKey: ['assessmentNotes', assessmentId],
    queryFn: () => assessmentNotesService.getAssessmentNotes(assessmentId),
  });

  // Create note mutation
  const createNoteMutation = useMutation({
    mutationFn: (data: CreateNoteData) => assessmentNotesService.createNote(assessmentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessmentNotes', assessmentId] });
      setNewNote({ noteType: 'general', content: '' });
      setShowAddForm(false);
    },
  });

  // Update note mutation
  const updateNoteMutation = useMutation({
    mutationFn: ({ noteId, data }: { noteId: number; data: { content: string; noteType?: string } }) =>
      assessmentNotesService.updateNote(noteId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessmentNotes', assessmentId] });
      setEditingNote(null);
    },
  });

  // Delete note mutation
  const deleteNoteMutation = useMutation({
    mutationFn: (noteId: number) => assessmentNotesService.deleteNote(noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessmentNotes', assessmentId] });
    },
  });

  const notes = notesData?.notes || [];

  const handleCreateNote = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newNote.content.trim()) return;
    createNoteMutation.mutate(newNote);
  };

  const handleUpdateNote = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingNote || !editingNote.content.trim()) return;
    updateNoteMutation.mutate({
      noteId: editingNote.id,
      data: { content: editingNote.content, noteType: editingNote.noteType },
    });
  };

  const handleDeleteNote = (noteId: number) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      deleteNoteMutation.mutate(noteId);
    }
  };

  const getNoteTypeColor = (noteType: string) => {
    switch (noteType) {
      case 'interview':
        return 'bg-info-100 text-info-800';
      case 'observation':
        return 'bg-success-100 text-success-800';
      case 'recommendation':
        return 'bg-accent-100 text-accent-800';
      default:
        return 'bg-neutral-100 text-neutral-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="md" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-400" />
          <h3 className="text-lg font-medium text-gray-900">Evaluation Notes</h3>
          <span className="bg-gray-100 text-gray-600 text-sm px-2 py-1 rounded-full">
            {notes.length}
          </span>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Add Note
        </button>
      </div>

      {/* Add Note Form */}
      {showAddForm && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="text-sm font-medium text-gray-900 mb-3">Add New Note</h4>
          <form onSubmit={handleCreateNote} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Note Type</label>
              <select
                value={newNote.noteType}
                onChange={(e) => setNewNote({ ...newNote, noteType: e.target.value })}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="general">General</option>
                <option value="interview">Interview</option>
                <option value="observation">Observation</option>
                <option value="recommendation">Recommendation</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
              <textarea
                value={newNote.content}
                onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                rows={3}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your note here..."
                required
              />
            </div>
            <div className="flex justify-end space-x-2">
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setNewNote({ noteType: 'general', content: '' });
                }}
                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={createNoteMutation.isLoading}
                className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {createNoteMutation.isLoading ? 'Adding...' : 'Add Note'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Notes List */}
      <div className="space-y-4">
        {notes.length === 0 ? (
          <div className="text-center py-8">
            <ChatBubbleLeftRightIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No notes yet</h3>
            <p className="mt-1 text-sm text-gray-500">Add your first note to get started.</p>
          </div>
        ) : (
          notes.map((note) => (
            <div key={note.id} className="bg-white border border-gray-200 rounded-lg p-4">
              {editingNote?.id === note.id ? (
                // Edit Form
                <form onSubmit={handleUpdateNote} className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Note Type</label>
                    <select
                      value={editingNote.noteType}
                      onChange={(e) => setEditingNote({ ...editingNote, noteType: e.target.value })}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="general">General</option>
                      <option value="interview">Interview</option>
                      <option value="observation">Observation</option>
                      <option value="recommendation">Recommendation</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                    <textarea
                      value={editingNote.content}
                      onChange={(e) => setEditingNote({ ...editingNote, content: e.target.value })}
                      rows={3}
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setEditingNote(null)}
                      className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={updateNoteMutation.isLoading}
                      className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                    >
                      {updateNoteMutation.isLoading ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </form>
              ) : (
                // Note Display
                <div>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getNoteTypeColor(note.noteType)}`}>
                          {note.noteType}
                        </span>
                        <div className="flex items-center text-sm text-gray-500">
                          <UserIcon className="h-4 w-4 mr-1" />
                          {note.advisor?.firstName || 'Unknown'} {note.advisor?.lastName || 'User'}
                        </div>
                        <div className="flex items-center text-sm text-gray-500">
                          <ClockIcon className="h-4 w-4 mr-1" />
                          {formatDate(note.createdAt)}
                        </div>
                      </div>
                      <p className="text-gray-900 whitespace-pre-wrap">{note.content}</p>
                    </div>
                    <div className="flex items-center space-x-1 ml-4">
                      <button
                        onClick={() => setEditingNote(note)}
                        className="p-1 text-gray-400 hover:text-gray-600"
                        title="Edit note"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title="Delete note"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default AssessmentNotes;
